import { useCallback, useEffect, useState } from "react";
import { Activity, Filter, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  listAuditActions,
  listAuditLogs,
  type AuditLogEntry,
} from "@/api/auditLogs";

/**
 * Audit log viewer (SUPER_ADMIN only).
 *
 * Mirrors the cursor-paginated `/audit-logs` API: each "Load more" click
 * appends the next page. Filters are debounced via the explicit "Apply"
 * button so we don't hammer the API on every keystroke.
 */
export function AuditLogSettings() {
  const { addToast } = useToast();

  // Filter form state — committed to `applied` only when the user clicks
  // Apply. This keeps the typing experience snappy and gives a single
  // round-trip per filter change.
  const [actionInput, setActionInput] = useState("");
  const [entityTypeInput, setEntityTypeInput] = useState("");
  const [entityIdInput, setEntityIdInput] = useState("");
  const [userIdInput, setUserIdInput] = useState("");

  const [applied, setApplied] = useState({
    action: "",
    entity_type: "",
    entity_id: "",
    user_id: "",
  });

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchPage = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      try {
        const filters: Record<string, string | number> = { limit: 50 };
        if (applied.action) filters.action = applied.action;
        if (applied.entity_type) filters.entity_type = applied.entity_type;
        if (applied.entity_id) filters.entity_id = applied.entity_id;
        if (applied.user_id) filters.user_id = applied.user_id;
        if (!reset && cursor) filters.before = cursor;

        const page = await listAuditLogs(filters);
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next_before);
        setHasMore(page.has_more);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load audit log";
        addToast("error", msg);
      } finally {
        setLoading(false);
      }
    },
    [applied, cursor, addToast],
  );

  // First load + reload whenever the applied filter changes. We deliberately
  // depend on `applied` (not `cursor`) so a manual "Load more" doesn't
  // re-trigger this effect.
  useEffect(() => {
    void fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  useEffect(() => {
    listAuditActions()
      .then(setActions)
      .catch(() => setActions([]));
  }, []);

  const onApply = () => {
    setApplied({
      action: actionInput.trim(),
      entity_type: entityTypeInput.trim(),
      entity_id: entityIdInput.trim(),
      user_id: userIdInput.trim(),
    });
  };

  const onClear = () => {
    setActionInput("");
    setEntityTypeInput("");
    setEntityIdInput("");
    setUserIdInput("");
    setApplied({ action: "", entity_type: "", entity_id: "", user_id: "" });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Audit log
          </CardTitle>
        </CardHeader>

        <div className="space-y-4 p-4">
          {/* Filter bar */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium">Action contains</label>
              <Input
                list="audit-actions"
                value={actionInput}
                placeholder="e.g. login.failed"
                onChange={(e) => setActionInput(e.target.value)}
              />
              <datalist id="audit-actions">
                {actions.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Entity type</label>
              <Input
                value={entityTypeInput}
                placeholder="employee"
                onChange={(e) => setEntityTypeInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Entity ID</label>
              <Input
                value={entityIdInput}
                placeholder="UUID or string"
                onChange={(e) => setEntityIdInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Actor user ID</label>
              <Input
                value={userIdInput}
                placeholder="UUID"
                onChange={(e) => setUserIdInput(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onApply} disabled={loading}>
              <Filter className="mr-2 h-4 w-4" /> Apply
            </Button>
            <Button variant="ghost" onClick={onClear} disabled={loading}>
              Clear
            </Button>
            <Button
              variant="ghost"
              onClick={() => void fetchPage(true)}
              disabled={loading}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>

          {/* Log table */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td className="px-3 py-6 text-center text-muted-foreground" colSpan={6}>
                      No audit entries match.
                    </td>
                  </tr>
                )}
                {items.map((row) => {
                  const isOpen = !!expanded[row.id];
                  return (
                    <tr key={row.id} className="border-t align-top">
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.user_id ?? "system"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge>{row.action}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{row.entity_type}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {row.entity_id ?? "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.ip_address ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))
                          }
                        >
                          {isOpen ? "Hide" : "Show"}
                        </Button>
                        {isOpen && (
                          <pre className="mt-2 max-w-xl overflow-auto rounded bg-muted/40 p-2 text-xs">
{JSON.stringify(
  { old: row.old_values ?? null, new: row.new_values ?? null },
  null,
  2,
)}
                          </pre>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Showing {items.length} entr{items.length === 1 ? "y" : "ies"}.
            </div>
            <Button
              variant="ghost"
              onClick={() => void fetchPage(false)}
              disabled={!hasMore || loading}
            >
              {loading ? "Loading…" : hasMore ? "Load more" : "End of log"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
